import { createContext, useContext, useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { useCardData } from '../internal/CardDataContext';
import { useCardLocationContext } from 'crm-cards-sdk/internal/CardLocationContext';
import { createResizeObserverManager } from '../internal/ResizeObserverManager';
import Raven from 'raven-js';
import { jsx as _jsx } from "react/jsx-runtime";
const observerManager = createResizeObserverManager();
export const CARD_SIZE = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large'
};
const LARGE_WIDTH = 780; // 780px card content width
const MEDIUM_WIDTH = 480; // 500px is larger than most sidebar max widths
// Anything below 500px is 'small'

const SCROLLBAR_BUFFER = 20;
const CardSizeContext = /*#__PURE__*/createContext(undefined);

/*
 * @param {number} px - Width of element in pixels
 * @param {CardSize} currentCardSize [o] - Current card size. This param makes it possible to handle an edge case where the size flickers when px is near a t-shirt size boundary
 */
function getTShirtSize(px, currentCardSize) {
  if (px > LARGE_WIDTH) {
    if (currentCardSize === 'medium' && px - LARGE_WIDTH < SCROLLBAR_BUFFER) {
      // Prevents flickering when the next size down doesn't have a scrollbar
      return currentCardSize;
    }
    return 'large';
  } else if (px > MEDIUM_WIDTH) {
    if (currentCardSize === 'small' && px - MEDIUM_WIDTH < SCROLLBAR_BUFFER) {
      // Prevents flickering when the next size down doesn't have a scrollbar
      return currentCardSize;
    }
    return 'medium';
  } else if (px > 0) {
    return 'small';
  }
}
function getCardElement(selector, location, loggingTag) {
  if (!selector) return;
  const elMatches = document.querySelectorAll(selector);
  if (elMatches.length > 1) {
    // If this becomes an issue, we can pass down a ref to the CrmCardWrapper instead of using selector
    Raven.captureException(new Error('useCardSize: Found multiple elements with the same selector'), {
      extra: {
        selector,
        location,
        loggingTag
      }
    });
  }
  return elMatches.length === 1 ? elMatches[0] : undefined;
}
function useResizeObserver(cardEl, setCardSize, cardSize) {
  // Use a ref so that `callback` can get the latest value of cardSize each time it runs, without needing to redefine `callback` whenever cardSize changes
  const cardSizeRef = useRef(cardSize);
  useEffect(() => {
    cardSizeRef.current = cardSize;
  }, [cardSize]);
  const callback = useCallback(entry => {
    let px;
    if (entry.contentBoxSize) {
      if (entry.contentBoxSize[0]) {
        px = entry.contentBoxSize[0].inlineSize;
      } else {
        // @ts-expect-error Compatibility for pre-92 Firefox
        px = entry.contentBoxSize.inlineSize;
      }
    } else {
      px = entry.contentRect.width;
    }
    if (px !== undefined) {
      const newCardSize = getTShirtSize(px, cardSizeRef.current);
      if (newCardSize) {
        setCardSize(newCardSize);
      }
    }
  }, [setCardSize]);
  useEffect(() => {
    if (cardEl) {
      observerManager.subscribe(cardEl, callback);
    }
    return () => {
      if (cardEl) {
        observerManager.unsubscribe(cardEl);
      }
    };
  }, [cardEl, callback]);
}
export function CardSizeContextProvider({
  children
}) {
  const {
    location,
    loggingTag
  } = useCardLocationContext();
  const {
    cardData
  } = useCardData();
  const cardElSelector = location && cardData ?
  // IMPORTANT: Because `cardId` might contain a slash ('/'), this cannot be written as "#cardId"
  // ALSO IMPORTANT: On an app like records there could be two of the same card in the right sidebar and middlepane, so we key by location
  `[data-crm-location="${location}"] [id="card-wrapper-${cardData.definition.cardId}"]` :
  // if no location, fall back to just the card selector (should never happen except maybe dev mode)
  cardData ? `[id="card-wrapper-${cardData.definition.cardId}"]` : null;

  // Card wrapper should already be rendered, so we can initialize both state values right away
  const [cardEl, setCardEl] = useState(() => getCardElement(cardElSelector, location, loggingTag));
  const [cardSize, setCardSize] = useState(() => {
    if (!cardEl) return undefined;
    return getTShirtSize(cardEl.getBoundingClientRect().width);
  });
  useResizeObserver(cardEl, setCardSize, cardSize);
  useEffect(() => {
    if (cardElSelector && !cardEl) {
      const element = getCardElement(cardElSelector, location, loggingTag);
      if (element) {
        setCardEl(element);
        setCardSize(getTShirtSize(element.getBoundingClientRect().width));
      }
    }
  }, [cardEl, cardElSelector, location, loggingTag, setCardEl, setCardSize]);
  const contextValue = useMemo(() => ({
    cardSize
  }), [cardSize]);
  return /*#__PURE__*/_jsx(CardSizeContext.Provider, {
    value: contextValue,
    children: children
  });
}
export function useCardSize() {
  const value = useContext(CardSizeContext);
  if (!value) {
    throw new Error('useCardSize was called outside of a valid CardSizeContext.');
  }
  return value.cardSize;
}